import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Chip,
    FormControlLabel,
    Checkbox,
    ToggleButtonGroup,
    ToggleButton,
    Tooltip,
    Stack,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RequestService from '../../services/RequestService';

const NewUserDialog = ({ open, onClose, onSuccess, user }) => {
    const [ownsEmail, setOwnsEmail] = useState('no');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        companyId: user.company.id,
        role: '',
        password: '',
        groupIds: [],
        isChild: false,
    });

    const [groups, setGroups] = useState([]);
    const [error, setError] = useState(null);
    const [permissionTypes, setPermissionTypes] = useState([]);

    const permissionLabels = {
        USER_VIEW: 'Visualizar Usuário',
        USER_CREATE: 'Criar Usuário',
        USER_UPDATE: 'Editar Usuário',
        USER_DELETE: 'Excluir Usuário',
        USER_AWARD: 'Premiar Usuário',
        USER_PENALTY: 'Penalizar Usuário',
        CARDS_VIEW: 'Visualizar Cartões',
        CARDS_ASSIGN: 'Atribuir Cartões',
        GROUP_VIEW: 'Visualizar Grupos',
        GROUP_CREATE: 'Criar Grupo',
        GROUP_UPDATE: 'Editar Grupo',
        GROUP_DELETE: 'Excluir Grupo',
        GAME_VIEW: 'Visualizar Jogos',
        GAME_CREATE: 'Criar Jogo',
        GAME_UPDATE: 'Editar Jogo',
        GAME_DELETE: 'Excluir Jogo',
        CONTENT_VIEW: 'Visualizar Conteúdos',
        CONTENT_CREATE: 'Criar Conteúdo',
        CONTENT_UPDATE: 'Editar Conteúdo',
        CONTENT_DELETE: 'Excluir Conteúdo',
        CONTENT_APPROVE: 'Aprovar Conteúdo',
        CONTENT_REVIEW: 'Revisar Comprovações',
        STORE_VIEW: 'Visualizar Lojas',
        STORE_CREATE: 'Criar Loja',
        STORE_UPDATE: 'Editar Loja',
        STORE_DELETE: 'Excluir Loja',
        STORE_APPROVE: 'Aprovar Retirada',
        SETTINGS_VIEW: 'Visualizar Configurações',
    };

    useEffect(() => {
        const fetchGroups = async () => {
            try {
                const groupData = await RequestService.getGroups(
                    user.company.id
                );
                setGroups(Array.isArray(groupData) ? groupData : []);
            } catch (err) {
                setGroups([]);
                setError('Erro ao carregar grupos.');
            }
        };

        if (user.company.id) {
            fetchGroups();
        }
    }, [user.company.id]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
            ...(name === 'isChild' && checked ? { email: '' } : {}),
        }));
    };

    const handleGroupsChange = (event) => {
        const {
            target: { value },
        } = event;

        setFormData((prev) => ({
            ...prev,
            groupIds: typeof value === 'string' ? value.split(',') : value,
        }));
    };

    const handleSubmit = async () => {
        setError(null);
        if (ownsEmail === 'yes' && !formData.email) {
            setError(
                'Informe um e-mail válido ou selecione "Não" em Possui e-mail.'
            );
            return;
        }

        const isChild = ownsEmail === 'no';

        try {
            const payload = {
                ...formData,
                isChild,
                ...(isChild ? {} : { email: formData.email }),
                groupIds: formData.groupIds,
                permissionTypes,
            };

            await RequestService.addPlayer(payload);

            setFormData({
                name: '',
                email: '',
                companyId: user.company.id,
                role: '',
                password: '',
                groupIds: [],
                isChild: false,
            });
            setOwnsEmail('no');
            setPermissionTypes([]);

            onSuccess();
            onClose();
        } catch (err) {
            if (err?.status === 409)
                setError('E-mail já cadastrado. Use outro e-mail.');
            else setError('Erro ao adicionar jogador. Verifique os dados.');
        }
    };

    const handleOwnsEmailChange = (_e, newValue) => {
        if (!newValue) return;
        setOwnsEmail(newValue);
        setError(null);

        if (newValue === 'no') {
            setFormData((prev) => ({
                ...prev,
                isChild: true,
                email: '',
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                isChild: false,
            }));
        }
    };

    const handlePermissionChange = (permission) => {
        setPermissionTypes((prev) =>
            prev.includes(permission)
                ? prev.filter((p) => p !== permission)
                : [...prev, permission]
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Novo Usuário</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="subtitle1">Dados</Typography>
                    <TextField
                        label="Nome"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        fullWidth
                    />
                    {/* 🔘 Possui e-mail próprio? (Toggle) */}
                    <Box>
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ mb: 1 }}
                        >
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                Possui e-mail próprio?
                            </Typography>
                            <Tooltip title="Caso o usuário não possua e-mail, você pode criar uma conta sem informar um e-mail. Nesse caso, o usuário poderá efetuar login usando o apelido ou QR Code.">
                                <HelpOutlineIcon
                                    color="primary"
                                    sx={{
                                        width: '18px',
                                        height: '18px',
                                    }}
                                />
                            </Tooltip>
                        </Stack>
                        <ToggleButtonGroup
                            value={ownsEmail}
                            exclusive
                            fullWidth
                            onChange={handleOwnsEmailChange}
                            size="small"
                        >
                            <ToggleButton value="yes">Sim</ToggleButton>
                            <ToggleButton value="no">Não</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {/* Campo de e-mail só aparece quando "Sim" */}
                    {ownsEmail === 'yes' && (
                        <TextField
                            label="E-mail"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            fullWidth
                        />
                    )}
                    {/* <TextField
                        label="E-mail"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={formData.isChild}
                        fullWidth
                    /> */}
                    <TextField
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        select
                        SelectProps={{ native: true }}
                        fullWidth
                    >
                        <option value="">Selecione</option>
                        <option value="ADMIN">Administrador</option>
                        <option value="MODERATOR">Moderador</option>
                        <option value="USER">Usuário</option>
                    </TextField>
                    <TextField
                        label="Senha"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleChange}
                        fullWidth
                    />
                    <FormControl fullWidth>
                        <InputLabel>Grupos</InputLabel>
                        <Select
                            multiple
                            value={formData.groupIds}
                            onChange={handleGroupsChange}
                            input={<OutlinedInput label="Grupos" />}
                            renderValue={(selected) =>
                                Array.isArray(groups)
                                    ? groups
                                          .filter((group) =>
                                              selected.includes(group.id)
                                          )
                                          .map((group) => (
                                              <Chip
                                                  key={group.id}
                                                  label={group.name}
                                              />
                                          ))
                                    : []
                            }
                        >
                            {Array.isArray(groups) &&
                                groups.map((group) => (
                                    <MenuItem key={group.id} value={group.id}>
                                        {group.name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>

                    {/* <FormControlLabel
                        control={
                            <Checkbox
                                name="isChild"
                                checked={formData.isChild}
                                onChange={handleChange}
                            />
                        }
                        label="Esse usuário não possui e-mail próprio"
                    /> */}

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 2,
                            backgroundColor: '#EDF8FF',
                            borderRadius: 2,
                        }}
                    >
                        <LockOutlinedIcon color="primary" />
                        <Typography variant="body2">
                            Ao clicar em criar usuário, uma mensagem com
                            instruções será enviada ao e-mail cadastrado.
                        </Typography>
                    </Box>

                    {formData.role === 'MODERATOR' && (
                        <Box
                            sx={{
                                maxHeight: 300,
                                overflowY: 'auto',
                                border: '1px solid #ddd',
                                borderRadius: 1,
                                padding: 2,
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Permissões
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    columnGap: 2,
                                    rowGap: 1,
                                }}
                            >
                                {Object.keys(permissionLabels).map(
                                    (permission) => (
                                        <FormControlLabel
                                            key={permission}
                                            control={
                                                <Checkbox
                                                    checked={permissionTypes.includes(
                                                        permission
                                                    )}
                                                    onChange={() =>
                                                        handlePermissionChange(
                                                            permission
                                                        )
                                                    }
                                                />
                                            }
                                            label={
                                                permissionLabels[permission] ||
                                                permission
                                            }
                                        />
                                    )
                                )}
                            </Box>
                        </Box>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined">
                    Fechar
                </Button>
                <Button
                    onClick={handleSubmit}
                    color="primary"
                    variant="contained"
                >
                    Criar Usuário
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NewUserDialog;
